// Biometric Authentication Service for BookAPT
// Handles fingerprint and Face ID authentication

import SimpleStorage from '../utils/simpleStorage';

export interface BiometricAuthResult {
  success: boolean;
  error?: string;
  biometryType?: 'TouchID' | 'FaceID' | 'Fingerprint' | 'Face' | 'Iris';
  errorCode?: string;
}

export interface BiometricSettings {
  isEnabled: boolean;
  biometryType: string;
  fallbackEnabled: boolean;
  maxAttempts: number;
  lockoutDuration: number; // in minutes
}

export interface BiometricCredentials {
  username: string;
  password: string;
  isBiometricEnabled: boolean;
  lastUsed: string;
}

class BiometricAuthService {
  private readonly BIOMETRIC_SETTINGS_KEY = 'biometric_settings';
  private readonly BIOMETRIC_CREDENTIALS_KEY = 'biometric_credentials';
  private readonly BIOMETRIC_ATTEMPTS_KEY = 'biometric_attempts';

  // Check if biometric authentication is available
  async isBiometricAvailable(): Promise<boolean> {
    try {
      // In a real app, this would use react-native-biometrics or similar
      // For demo purposes, simulate availability check
      console.log('Checking biometric availability...');
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Simulate 90% availability rate
      return Math.random() > 0.1;
    } catch (error) {
      console.error('Error checking biometric availability:', error);
      return false;
    }
  }

  // Get available biometric types
  async getAvailableBiometricTypes(): Promise<string[]> {
    try {
      // In a real app, this would detect actual biometric types
      const availableTypes = [];
      
      // Simulate detection
      if (Math.random() > 0.3) {
        availableTypes.push('TouchID');
      }
      if (Math.random() > 0.5) {
        availableTypes.push('FaceID');
      }
      if (availableTypes.length === 0) {
        availableTypes.push('Fingerprint');
      }
      
      return availableTypes;
    } catch (error) {
      console.error('Error getting biometric types:', error);
      return ['Fingerprint'];
    }
  }

  // Enable biometric authentication
  async enableBiometricAuth(
    username: string,
    password: string,
    biometryType: string
  ): Promise<BiometricAuthResult> {
    try {
      // Check if biometric is available
      const isAvailable = await this.isBiometricAvailable();
      if (!isAvailable) {
        return {
          success: false,
          error: 'Biometric authentication is not available on this device'
        };
      }

      // Check if already enabled
      const existingCredentials = await this.getBiometricCredentials();
      if (existingCredentials && existingCredentials.isBiometricEnabled) {
        return {
          success: false,
          error: 'Biometric authentication is already enabled'
        };
      }

      // Save credentials securely
      const credentials: BiometricCredentials = {
        username,
        password,
        isBiometricEnabled: true,
        lastUsed: new Date().toISOString()
      };

      await this.saveBiometricCredentials(credentials);

      // Update settings
      const settings: BiometricSettings = {
        isEnabled: true,
        biometryType,
        fallbackEnabled: true,
        maxAttempts: 3,
        lockoutDuration: 5
      };

      await this.saveBiometricSettings(settings);

      return {
        success: true,
        biometryType: biometryType as any
      };
    } catch (error) {
      console.error('Error enabling biometric auth:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to enable biometric authentication'
      };
    }
  }

  // Disable biometric authentication
  async disableBiometricAuth(): Promise<BiometricAuthResult> {
    try {
      // Clear stored credentials
      await this.clearBiometricCredentials();

      // Update settings
      const settings: BiometricSettings = {
        isEnabled: false,
        biometryType: '',
        fallbackEnabled: false,
        maxAttempts: 3,
        lockoutDuration: 5
      };

      await this.saveBiometricSettings(settings);

      return {
        success: true
      };
    } catch (error) {
      console.error('Error disabling biometric auth:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to disable biometric authentication'
      };
    }
  }

  // Authenticate with biometric
  async authenticateWithBiometric(): Promise<BiometricAuthResult> {
    try {
      // Check if biometric is enabled
      const settings = await this.getBiometricSettings();
      if (!settings.isEnabled) {
        return {
          success: false,
          error: 'Biometric authentication is not enabled'
        };
      }

      // Check if user is locked out
      const isLockedOut = await this.isUserLockedOut();
      if (isLockedOut) {
        return {
          success: false,
          error: 'Too many failed attempts. Please try again later.',
          errorCode: 'LOCKED_OUT'
        };
      }

      // Simulate biometric authentication
      console.log(`Authenticating with ${settings.biometryType}...`);
      
      // Simulate authentication process
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Simulate 95% success rate
      const isSuccess = Math.random() > 0.05;
      
      if (isSuccess) {
        // Update last used time
        const credentials = await this.getBiometricCredentials();
        if (credentials) {
          credentials.lastUsed = new Date().toISOString();
          await this.saveBiometricCredentials(credentials);
        }

        // Clear failed attempts
        await this.clearFailedAttempts();

        return {
          success: true,
          biometryType: settings.biometryType as any
        };
      } else {
        // Record failed attempt
        await this.recordFailedAttempt();
        
        return {
          success: false,
          error: 'Biometric authentication failed. Please try again.',
          errorCode: 'AUTHENTICATION_FAILED'
        };
      }
    } catch (error) {
      console.error('Error authenticating with biometric:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Biometric authentication failed'
      };
    }
  }

  // Get biometric settings
  async getBiometricSettings(): Promise<BiometricSettings> {
    try {
      const settingsJson = await SimpleStorage.getItem(this.BIOMETRIC_SETTINGS_KEY);
      
      if (settingsJson) {
        return JSON.parse(settingsJson);
      }

      // Default settings
      return {
        isEnabled: false,
        biometryType: '',
        fallbackEnabled: false,
        maxAttempts: 3,
        lockoutDuration: 5
      };
    } catch (error) {
      console.error('Error getting biometric settings:', error);
      return {
        isEnabled: false,
        biometryType: '',
        fallbackEnabled: false,
        maxAttempts: 3,
        lockoutDuration: 5
      };
    }
  }

  // Get biometric credentials
  async getBiometricCredentials(): Promise<BiometricCredentials | null> {
    try {
      const credentialsJson = await SimpleStorage.getItem(this.BIOMETRIC_CREDENTIALS_KEY);
      return credentialsJson ? JSON.parse(credentialsJson) : null;
    } catch (error) {
      console.error('Error getting biometric credentials:', error);
      return null;
    }
  }

  // Check if user is locked out
  async isUserLockedOut(): Promise<boolean> {
    try {
      const attemptsJson = await SimpleStorage.getItem(this.BIOMETRIC_ATTEMPTS_KEY);
      const attempts = attemptsJson ? JSON.parse(attemptsJson) : { count: 0, lastAttempt: null };
      
      const settings = await this.getBiometricSettings();
      const now = new Date();
      const lastAttempt = attempts.lastAttempt ? new Date(attempts.lastAttempt) : null;
      
      // Check if lockout period has expired
      if (lastAttempt) {
        const lockoutDuration = settings.lockoutDuration * 60 * 1000; // Convert to milliseconds
        const timeSinceLastAttempt = now.getTime() - lastAttempt.getTime();
        
        if (timeSinceLastAttempt < lockoutDuration) {
          return attempts.count >= settings.maxAttempts;
        } else {
          // Lockout period expired, reset attempts
          await this.clearFailedAttempts();
          return false;
        }
      }
      
      return attempts.count >= settings.maxAttempts;
    } catch (error) {
      console.error('Error checking lockout status:', error);
      return false;
    }
  }

  // Record failed attempt
  private async recordFailedAttempt(): Promise<void> {
    try {
      const attemptsJson = await SimpleStorage.getItem(this.BIOMETRIC_ATTEMPTS_KEY);
      const attempts = attemptsJson ? JSON.parse(attemptsJson) : { count: 0, lastAttempt: null };
      
      attempts.count += 1;
      attempts.lastAttempt = new Date().toISOString();
      
      await SimpleStorage.setItem(this.BIOMETRIC_ATTEMPTS_KEY, JSON.stringify(attempts));
    } catch (error) {
      console.error('Error recording failed attempt:', error);
    }
  }

  // Clear failed attempts
  private async clearFailedAttempts(): Promise<void> {
    try {
      await SimpleStorage.setItem(this.BIOMETRIC_ATTEMPTS_KEY, JSON.stringify({ count: 0, lastAttempt: null }));
    } catch (error) {
      console.error('Error clearing failed attempts:', error);
    }
  }

  // Save biometric settings
  private async saveBiometricSettings(settings: BiometricSettings): Promise<void> {
    try {
      await SimpleStorage.setItem(this.BIOMETRIC_SETTINGS_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving biometric settings:', error);
    }
  }

  // Save biometric credentials
  private async saveBiometricCredentials(credentials: BiometricCredentials): Promise<void> {
    try {
      await SimpleStorage.setItem(this.BIOMETRIC_CREDENTIALS_KEY, JSON.stringify(credentials));
    } catch (error) {
      console.error('Error saving biometric credentials:', error);
    }
  }

  // Clear biometric credentials
  private async clearBiometricCredentials(): Promise<void> {
    try {
      await SimpleStorage.removeItem(this.BIOMETRIC_CREDENTIALS_KEY);
    } catch (error) {
      console.error('Error clearing biometric credentials:', error);
    }
  }

  // Get biometric authentication status
  async getBiometricStatus(): Promise<{
    isAvailable: boolean;
    isEnabled: boolean;
    biometryType: string;
    canUseBiometric: boolean;
  }> {
    try {
      const isAvailable = await this.isBiometricAvailable();
      const settings = await this.getBiometricSettings();
      const isLockedOut = await this.isUserLockedOut();
      
      return {
        isAvailable,
        isEnabled: settings.isEnabled,
        biometryType: settings.biometryType,
        canUseBiometric: isAvailable && settings.isEnabled && !isLockedOut
      };
    } catch (error) {
      console.error('Error getting biometric status:', error);
      return {
        isAvailable: false,
        isEnabled: false,
        biometryType: '',
        canUseBiometric: false
      };
    }
  }

  // Validate biometric credentials
  async validateBiometricCredentials(username: string, password: string): Promise<boolean> {
    try {
      const credentials = await this.getBiometricCredentials();
      if (!credentials) {
        return false;
      }

      return credentials.username === username && credentials.password === password;
    } catch (error) {
      console.error('Error validating biometric credentials:', error);
      return false;
    }
  }
}

export const biometricAuthService = new BiometricAuthService();
export default BiometricAuthService;
